import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../../api/client'
import type { MaintenanceWindow, PatchChange, PatchTask } from '../../api/client'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { ErrorBanner, Spinner, formatDateTime } from '../common'
import { DataTable } from '../DataTable'
import {
  TaskStatusChip, VerificationChip, kindLabel, platformLabel, rebootPolicy, taskStatus, useIsTenantAdmin,
  verificationLabel,
} from './labels'

/**
 * One installation on one machine, readable: what was asked, what the agent
 * found and changed (from → to), whether the verification confirmed it, and
 * the full log at the end for whoever needs it.
 */
export function TaskDetail({ taskId, onClose, onChanged }: {
  taskId: string
  onClose: () => void
  onChanged?: () => void
}) {
  const task = useAsync(() => api.patchTask(taskId), [taskId])
  const admin = useIsTenantAdmin()
  const [error, setError] = useState<unknown>(null)
  const [showLog, setShowLog] = useState(false)

  async function act(fn: () => Promise<unknown>) {
    setError(null)
    try {
      await fn()
      await task.reload()
      onChanged?.()
    } catch (e) {
      setError(e)
    }
  }

  const r = task.data
  const changes = ((r?.result?.changes as PatchChange[] | undefined) ?? [])
  const dryChanges = ((r?.dry_run?.changes as PatchChange[] | undefined) ?? [])
  const summary = String(r?.result?.summary ?? '')
  const status = r ? taskStatus(r) : null

  return (
    <div className="dob-modal" role="dialog" aria-modal="true" aria-label={t('Installazione')}>
      <div className="dob-modal__scrim" onClick={onClose} />
      <div className="dob-modal__pannello">
        <div className="dob-modal__testata">
          <div>
            <h3 className="dob-modal__titolo">
              {r ? <><Link to={`/cis/${r.ci_id}`}>{r.ci_name ?? r.ci_id.slice(0, 8)}</Link> · {kindLabel(r.kind)}</> : t('Installazione')}
            </h3>
            {r && (
              <p className="dob-modal__sotto">
                {platformLabel(r.platform)}
                {r.result?.manager ? ` · ${String(r.result.manager)}` : ''}
                {' · '}{rebootPolicy(r.allow_reboot, Number(r.spec?.reboot_delay_min ?? 10))}
              </p>
            )}
          </div>
          <div className="dob-modal__comandi">
            {r && <TaskActions task={r} admin={admin} act={act} />}
            <button type="button" className="secondary" onClick={onClose}>{t('Chiudi')}</button>
          </div>
        </div>
        <ErrorBanner error={error ?? task.error} />
        {task.loading && !r ? <Spinner /> : r && status && (
          <div className="dob-modal__corpo">
            <p>
              <TaskStatusChip task={r} /> <VerificationChip v={r.verification} />
              {status.hint && <span className="text-muted"> {status.hint}</span>}
            </p>
            {summary && <p><strong>{summary}</strong></p>}
            {r.verification && <p className="text-muted">{verificationLabel(r.verification)}</p>}
            <Requested task={r} />
            <dl className="patch-times">
              {r.approved_at && <><dt>{t('Approvata')}</dt><dd>{formatDateTime(r.approved_at)} · {r.approved_by}</dd></>}
              {r.started_at && <><dt>{t('Iniziata')}</dt><dd>{formatDateTime(r.started_at)}</dd></>}
              {r.finished_at && <><dt>{t('Finita')}</dt><dd>{formatDateTime(r.finished_at)}</dd></>}
              {Boolean(r.result?.rebooted_at) && <><dt>{t('Riavviata')}</dt><dd>{formatDateTime(String(r.result.rebooted_at))}</dd></>}
            </dl>
            {changes.length > 0 && <ChangeTable title={t('Cosa è cambiato ({n})', { n: changes.length })} rows={changes} />}
            {changes.length === 0 && dryChanges.length > 0 && (
              <ChangeTable title={t('Cosa installerebbe (prova) ({n})', { n: dryChanges.length })} rows={dryChanges} />
            )}
            {r.dry_run && dryChanges.length === 0 && changes.length === 0 && (
              <p className="text-muted">{t('La prova dice: niente da installare, la macchina è già aggiornata.')}</p>
            )}
            {r.log && (
              <>
                <button type="button" className="link" onClick={() => setShowLog((v) => !v)}>
                  {showLog ? t('Nascondi il log') : t('Mostra il log completo')}
                </button>
                {showLog && <pre className="patch-log">{r.log}</pre>}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function Requested({ task }: { task: PatchTask }) {
  const kbs = (task.spec?.kbs as string[] | undefined) ?? (task.spec?.kb ? [String(task.spec.kb)] : [])
  const pkgs = (task.spec?.packages as Array<{ name: string; to?: string }> | undefined) ?? []
  if (task.kind === 'kb' && kbs.length) return <p>{t('Chiesto')}: <span className="mono">{kbs.join(', ')}</span></p>
  const programs = (task.spec?.products as string[] | undefined) ?? []
  if (task.kind === 'software' && programs.length) return <p>{t('Chiesto')}: <span className="mono">{programs.join(', ')}</span></p>
  if (task.kind === 'packages' && pkgs.length) {
    return <p>{t('Chiesto')}: <span className="mono">{pkgs.map((p) => p.name + (p.to ? ` → ${p.to}` : '')).join(', ')}</span></p>
  }
  return null
}

function resultLabel(r?: string): string {
  if (!r) return ''
  if (r === 'installed') return t('installato')
  if (r === 'unchanged') return t('non cambiato')
  if (r === 'not_found') return t('non offerto da winget né da Chocolatey')
  if (r === 'requested') return t('chiesto all’aggiornatore del produttore')
  if (r.startsWith('failed')) return t('non riuscito') + r.slice(6)
  return r
}

function ChangeTable({ title, rows }: { title: string; rows: PatchChange[] }) {
  return (
    <>
      <h4 className="patch-machine__title">{title}</h4>
      <DataTable
        rows={rows}
        rowKey={(c, i) => `${c.name}-${i}`}
        conteggio={{ nome: t('aggiornamenti') }}
        columns={[
          {
            key: 'name', header: t('Aggiornamento'),
            value: (c) => [c.name, c.security ? t('sicurezza') : '', c.title && c.title !== c.name ? c.title : ''].filter(Boolean).join(' '),
            render: (c) => (
              <>
                <span className="mono">{c.name}</span>
                {c.security && <> <span className="chip patch-chip patch-chip--info">{t('sicurezza')}</span></>}
                {c.title && c.title !== c.name && <div className="text-muted patch-sub">{c.title}</div>}
              </>
            ),
          },
          { key: 'from', header: t('Da'), mono: true, value: (c) => c.from || '', render: (c) => c.from || '' },
          { key: 'to', header: t('A'), mono: true, value: (c) => c.to || '', render: (c) => c.to || '' },
          { key: 'result', header: t('Esito'), value: (c) => resultLabel(c.result), render: (c) => resultLabel(c.result) },
        ]}
      />
    </>
  )
}

/** Kinds without a rollback: a reboot, and programs (winget and Chocolatey do not go back). */
const NO_ROLLBACK = ['reboot', 'software', 'software_all']

/** What can be done on a task outside a job (from the machine's page). */
function TaskActions({ task, admin, act }: {
  task: PatchTask; admin: boolean; act: (fn: () => Promise<unknown>) => Promise<void>
}) {
  const [approving, setApproving] = useState(false)
  if (task.job_id) {
    // Inside a job the job decides (approve, cancel, retry): one place only.
    return task.status === 'applied' && admin && !NO_ROLLBACK.includes(task.kind)
      ? <button className="secondary" onClick={() => act(() => api.patchRollback(task.id))}>{t('Rollback')}</button>
      : null
  }
  if (task.kind !== 'rollback' && ['proposed', 'approved'].includes(task.status)) {
    return (
      <>
        {task.kind !== 'reboot' && (
          <button className="secondary" onClick={() => act(() => api.patchDryRun(task.id))}>{t('Prova')}</button>
        )}
        {admin && task.status === 'proposed' && !approving && (
          <button onClick={() => setApproving(true)}>{t('Approva')}</button>
        )}
        {admin && approving && <ApproveInline task={task} act={act} onDone={() => setApproving(false)} />}
        {admin && <button className="secondary" onClick={() => act(() => api.patchCancel(task.id))}>{t('Annulla')}</button>}
      </>
    )
  }
  if (task.kind === 'rollback' && task.status === 'proposed' && admin) {
    return <button onClick={() => act(() => api.patchApprove(task.id, {}))}>{t('Approva il rollback')}</button>
  }
  if (admin && task.status === 'scheduled') {
    return <button className="secondary" onClick={() => act(() => api.patchCancel(task.id))}>{t('Annulla')}</button>
  }
  if (admin && ['applied', 'needs_reboot'].includes(task.status) && !NO_ROLLBACK.includes(task.kind) && task.kind !== 'rollback') {
    return <button className="secondary" onClick={() => act(() => api.patchRollback(task.id))}>{t('Rollback')}</button>
  }
  return null
}

function ApproveInline({ task, act, onDone }: {
  task: PatchTask; act: (fn: () => Promise<unknown>) => Promise<void>; onDone: () => void
}) {
  const windows = useAsync<MaintenanceWindow[]>(() => api.patchWindows(), [])
  const [windowId, setWindowId] = useState('')
  const [reboot, setReboot] = useState(false)
  return (
    <span className="patch-approve">
      <select value={windowId} onChange={(e) => setWindowId(e.target.value)} aria-label={t('Quando')}>
        <option value="">{t('subito')}</option>
        {(windows.data ?? []).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
      </select>
      <label><input type="checkbox" checked={reboot} onChange={(e) => setReboot(e.target.checked)} /> {t('riavvio se serve')}</label>
      <button onClick={() => act(() => api.patchApprove(task.id, {
        window_id: windowId || undefined, allow_reboot: reboot,
      })).then(onDone)}>{t('Conferma')}</button>
    </span>
  )
}
